import { RoleEnum } from '../enums/roleEnum';

/**
 * The two permissions on AdminInvitationsController (Permissions.cs). They are separate because the acts
 * are not equally dangerous: reading the audit trail is looking at who introduced whom, while revoke and
 * resend change what a token already in circulation does — and resend invalidates the old deep link
 * without notifying anybody.
 *
 * `admin.superadmin` and a full Admin pass both; AuthService.hasAdminPermission() handles that. So these
 * only bite for SubAdmins — test the gating as one.
 *
 * Added to the backend by spec decision 12.8 (2026-10-05). Before that the controller keyed on user type
 * alone and `Permissions.cs` had no invitations entry, so this gate made the screen Admin-only in
 * practice: no SubAdmin could hold a permission that did not exist. Granting it is therefore what lets a
 * SubAdmin in, not what shuts one out — but a grant only takes effect at their next sign-in, because
 * permission claims are written into the JWT at login.
 */
export const AdminInvitationPermissions = {
  View: 'invitations.view',
  /**
   * Revoke and resend. Gated per button rather than per route: reading the list needs only View, so a
   * SubAdmin with just that gets a read-only screen instead of two controls the API answers 403 to.
   */
  Manage: 'invitations.manage',
} as const;

// The route and the sidenav item both spread ADMIN_INVITATIONS_ACCESS, so what a SubAdmin can see and
// what they can open cannot drift apart. `permission` wins over `permissionGroup` in both roleGuard and
// the sidenav, and a full Admin always passes it. Write actions are gated per button on Manage.
export const ADMIN_INVITATIONS_ACCESS = {
  roles: [RoleEnum.Admin, RoleEnum.SubAdmin],
  permission: AdminInvitationPermissions.View,
};
